import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import { GoogleMap, Marker } from '@react-google-maps/api';
import * as React from 'react';
import { LocationSummary } from '../../app/ApiGen';
import locationImg from '../../assets/map/location.png';
import { InputComponentProps, ValueComponentProps } from '../../design-system';
import { getNestedHelperText, toChangeEvent } from '../../utils/formUtils';
import { toCoordinates } from '../../utils/locations/coordinates';
import { Normalised } from '../../utils/request';
import { GoogleMapLoader, googleMapStyles } from '../GoogleMap';

const mapContainerStyle: React.CSSProperties = {
  height: '160px',
  width: '100%',
};

export const PositionValue: React.FC<ValueComponentProps<Normalised<LocationSummary>['coordinates']>> = ({
  value: { latitude, longitude },
}) => {
  const pos = { lat: Number(latitude), lng: Number(longitude) };

  return (
    <GoogleMapLoader>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={pos}
        zoom={15}
        options={{
          styles: googleMapStyles,
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
          zoomControl: false,
        }}
      >
        <Marker position={pos} icon={locationImg} />
      </GoogleMap>
    </GoogleMapLoader>
  );
};

interface PositionInputProps extends InputComponentProps<Normalised<LocationSummary>['coordinates']> {
  zoom?: number;
}

export const PositionInput: React.FC<PositionInputProps> = ({
  value: { latitude, longitude },
  zoom = 15,
  onChange,
  disabled,
  error,
  helperText,
}) => {
  const pos = { lat: Number(latitude), lng: Number(longitude) };

  const [coordinatesText, setCoordinatesText] = React.useState<string>(`${latitude},${longitude}`);

  React.useEffect(() => {
    onChange('coordinates')(
      toChangeEvent<Normalised<LocationSummary>['coordinates']>(new Event('change'), toCoordinates(coordinatesText)),
    );
  }, [coordinatesText, onChange]);

  return (
    <Box width="100%">
      <GoogleMapLoader>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={pos}
          zoom={zoom}
          options={{ styles: googleMapStyles, streetViewControl: false }}
        >
          <Marker
            draggable={!disabled}
            icon={locationImg}
            onDragEnd={(e) => {
              if (!e.latLng) {
                return;
              }
              onChange('coordinates')(
                toChangeEvent<Normalised<LocationSummary>['coordinates']>(e, {
                  latitude: e.latLng.lat().toFixed(7),
                  longitude: e.latLng.lng().toFixed(7),
                }),
              );
            }}
            position={pos}
          >
            <input
              aria-label="Position"
              hidden
              onChange={(e) => setCoordinatesText(e.target.value)}
              type="text"
              value={coordinatesText}
            />
          </Marker>
        </GoogleMap>
      </GoogleMapLoader>
      <FormHelperText error={error}>
        {getNestedHelperText(helperText) ?? 'Drag the marker to pinpoint your location.'}
      </FormHelperText>
    </Box>
  );
};
